import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import styles from './FinancePage.module.css';

const MONTH_LABELS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function monthLabel(ym: string) {
  const [y, m] = ym.split('-');
  return `${MONTH_LABELS[parseInt(m) - 1]} ${y}`;
}

interface DashboardData {
  totalEnrolled: number;
  incomeWithoutScholarship: number;
  incomeWithScholarship: number;
  totalIncome: number;
  incomeByGrade: { grade: string; studentCount: number; income: number }[];
  topDebtors: { studentName: string; grade: string; debt: number }[];
  monthlyBreakdown: { month: string; expected: number; received: number; missing: number }[];
  activeMonths: string[];
  tuitionCost: number;
}

export default function FinanceDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<DashboardData>('/api/fees/dashboard')
      .then((d) => setData(d))
      .catch(() => setError('Error al cargar el tablero'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>Cargando tablero...</div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '2rem', color: '#c00' }}>
        {error || 'No se pudieron cargar los datos'}
      </div>
    );
  }

  const fmt = (n: number) =>
    `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className={styles.container}>
      <h1 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1a1c2c' }}>
        📊 Tablero de Finanzas
      </h1>

      {/* === Indicators === */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
        }}
      >
        <Card title="Alumnos Inscritos" value={data.totalEnrolled.toString()} />
        <Card title="Ingreso sin beca" value={fmt(data.incomeWithoutScholarship)} sub="Por mes" />
        <Card title="Ingreso con beca" value={fmt(data.incomeWithScholarship)} sub="Por mes" />
        <Card title="Ingreso Total" value={fmt(data.totalIncome)} sub="Por mes" highlight />
      </div>

      {/* === Income by Grade === */}
      <Section title="Ingresos por Grado">
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Grado</th>
              <th>Alumnos</th>
              <th>Ingreso mensual</th>
            </tr>
          </thead>
          <tbody>
            {data.incomeByGrade.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center', color: '#999' }}>
                  Sin datos
                </td>
              </tr>
            ) : (
              data.incomeByGrade.map((g) => (
                <tr key={g.grade}>
                  <td>
                    <strong>{g.grade}</strong>
                  </td>
                  <td>{g.studentCount}</td>
                  <td>{fmt(g.income)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Section>

      {/* === Top 5 Debtors === */}
      <Section title="Top 5 — Alumnos con Mayor Adeudo">
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Alumno</th>
              <th>Grado</th>
              <th>Adeudo total</th>
            </tr>
          </thead>
          <tbody>
            {data.topDebtors.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>
                  Sin adeudos
                </td>
              </tr>
            ) : (
              data.topDebtors.map((d, i) => (
                <tr key={i}>
                  <td style={{ color: '#999' }}>{i + 1}</td>
                  <td>
                    <strong>{d.studentName}</strong>
                  </td>
                  <td>{d.grade}</td>
                  <td style={{ color: d.debt > 0 ? '#c62828' : '#2e7d32', fontWeight: 700 }}>
                    {d.debt > 0 ? fmt(d.debt) : '✓ Al día'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Section>

      {/* === Monthly Breakdown === */}
      <Section title="Ingresos por Mes">
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Mes</th>
              <th>Esperado</th>
              <th>Recibido</th>
              <th>Faltante</th>
            </tr>
          </thead>
          <tbody>
            {data.monthlyBreakdown.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>
                  No hay meses configurados
                </td>
              </tr>
            ) : (
              data.monthlyBreakdown.map((m) => {
                const pct = m.expected > 0 ? ((m.received / m.expected) * 100).toFixed(0) : '0';
                return (
                  <tr key={m.month}>
                    <td>
                      <strong>{monthLabel(m.month)}</strong>
                    </td>
                    <td>{fmt(m.expected)}</td>
                    <td style={{ color: m.received > 0 ? '#2e7d32' : '#999' }}>
                      {m.received > 0 ? fmt(m.received) : '—'}
                    </td>
                    <td>
                      {m.missing > 0 ? (
                        <span style={{ color: '#c62828', fontWeight: 700 }}>{fmt(m.missing)}</span>
                      ) : (
                        <span style={{ color: '#2e7d32' }}>✓ Completado</span>
                      )}
                      <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.5rem' }}>
                        ({pct}% cobrado)
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Internal components                                                  */
/* ------------------------------------------------------------------ */

function Card({
  title,
  value,
  sub,
  highlight,
}: {
  title: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: highlight ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#fff',
        color: highlight ? '#fff' : '#1a1c2c',
        padding: '1.5rem',
        borderRadius: 16,
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      }}
    >
      <h3 style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.8, margin: 0 }}>{title}</h3>
      <p style={{ fontSize: '2rem', fontWeight: 700, margin: '0.3rem 0' }}>{value}</p>
      {sub && <p style={{ fontSize: '0.75rem', opacity: 0.7, margin: 0 }}>{sub}</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        padding: '1.5rem',
      }}
    >
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1c2c', marginBottom: '1rem' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}
